import React, { memo } from 'react';
import { Stack, Typography, Icon, Tooltip, Divider } from 'investira.react.components';
import { displays } from 'investira.react.lib';
import { formats } from 'investira.sdk';

const AvaliacoesOutputResumo = memo(props => {
  const { ativo, gestor, nota = null, notas = {}, resumo, resumoMock = {} } = props;

  const pvTemValor = pValue => pValue !== null && pValue !== undefined && pValue !== '';

  const pvFormatarNumero = (pValue, pDecimais = 2, pMoeda = false) => {
    return pvTemValor(pValue) ? formats.formatNumber(pValue, pDecimais, true, pMoeda) : '';
  };

  const pvFormatarPercentual = pValue => {
    return pvTemValor(pValue) ? `${pvFormatarNumero(pValue)}%` : '';
  };

  const dadosAtivo = [
    { label: 'Fundo', value: ativo?.ativo },
    { label: 'CNPJ', value: ativo?.cnpj ? displays.cnpj(ativo.cnpj) : '' },
    { label: 'ISIN', value: ativo?.isin },
    { label: 'Benchmark', value: ativo?.benchmark_indicador_id },
    { label: 'Aplicação mínima', value: pvFormatarNumero(ativo?.aplicacao_min, 2, true) },
    { label: 'Retorno', value: pvFormatarNumero(ativo?.retorno) },
    { label: 'Sharpe', value: pvFormatarNumero(ativo?.sharpe) },
    { label: 'Tracking error', value: pvFormatarPercentual(ativo?.tracking_error) },
    { label: 'Volatilidade', value: pvFormatarPercentual(ativo?.volatilidade) },
    { label: 'Patrimônio líquido', value: pvFormatarNumero(ativo?.pl, 2, true) },
    { label: 'Cotistas', value: pvFormatarNumero(ativo?.cotistas, 0) }
  ].filter(pItem => pvTemValor(pItem.value));

  const addressToString = pEndereco => {
    if (!pEndereco) {
      return null;
    }

    const { bairro = '', cep = '', cidade = '', complemento = '', logradouro = '' } = pEndereco;

    const xEndereco = `${logradouro || ''} ${complemento || ''}, ${bairro || ''}, ${
      cidade || ''
    } - ${displays.cep(cep)}`;

    return xEndereco;
  };

  const dadosGestor = [
    { label: 'Razão social', value: gestor?.pessoa || gestor?.pessoa_fantasia },
    { label: 'CNPJ', value: gestor?.cnpj ? displays.cnpj(gestor?.cnpj) : '' },
    {
      label: 'Fundação',
      value: gestor?.juridica?.constituicao
        ? formats.formatDateCustom(gestor?.juridica?.constituicao, 'DD/MM/YYYY')
        : ''
    },
    { label: 'Site', value: gestor?.infos?.site },
    { label: 'Telefone', value: gestor?.infos?.telefone },
    { label: 'Endereço', value: addressToString(gestor?.endereco) }
  ].filter(pItem => pItem.value !== null && pItem.value !== undefined && pItem.value !== '');

  const xBorderColor =
    nota === null || nota === undefined
      ? 'divider'
      : nota < 2
      ? 'error.main'
      : nota < 3.5
      ? 'warning.main'
      : nota < 4
      ? 'info.main'
      : 'primary.main';

  const pvFormatarNota = pNota => {
    return formats.formatNumber(Number(pNota || 0), 2, true, false);
  };

  return (
    <Stack direction="row" gap={4}>
      <Stack>
        <Stack gap={2} direction="row" alignItems="center" justifyContent="space-between" mb={1}>
          <Stack direction="row" gap={2} alignItems="baseline">
            <Tooltip title="Resumo gerado por IA" placement="top">
              <span>
                <Icon iconName="text_ia" size={21} color="primary" />
              </span>
            </Tooltip>
            <Typography variant="h6" color="textPrimary">
              Resumo
            </Typography>
          </Stack>
        </Stack>
        {resumo ? (
          <Typography variant="body2" color="textPrimary" gutterBottom>
            {resumo}
          </Typography>
        ) : (
          <>
            <Typography variant="body2" color="textPrimary" gutterBottom>
              Sob a ótica da avaliação quantitativa o fundo apresentou{' '}
              <b>{resumoMock.desempenhoQuantitativo}</b> desempenho nos últimos{' '}
              <b>{resumoMock.periodoMeses}</b> meses, obtendo a{' '}
              <b>
                {resumoMock.rankingPosicao}ª colocação no ranking de {resumoMock.rankingTotal}{' '}
                fundos de sua categoria
              </b>
              . Isto resultou em uma nota de avaliação quantitativa de{' '}
              <b>{pvFormatarNota(notas.notaQuantitativa)}</b>.
            </Typography>
            <Typography variant="body2" color="textPrimary" gutterBottom>
              Do ponto de vista da avaliação qualitativa, o fundo demonstra{' '}
              <b>{resumoMock.capacidadeFiduciaria}</b> capacidade de cumprimento do dever
              fiduciário, com <b>{resumoMock.garantiaConflitos}</b> garantia de tratamento de
              conflitos de interesses e observância a aspectos ASG e fundamentado em regulamento que
              assegura <b>{resumoMock.atendimentoRegulamento}</b> o atendimento dos interesses do
              cotista. A nota de avaliação qualitativa obtida foi{' '}
              <b>{pvFormatarNota(notas.notaQualitativa)}</b>. No geral, o fundo obteve nota{' '}
              <b>{pvFormatarNota(notas.notaGeral)}</b>.
            </Typography>
            <Typography variant="body2" color="textPrimary" gutterBottom>
              De acordo com o resultado geral, a seguinte possibilidade deve ser considerada:
            </Typography>
          </>
        )}
        <Stack gap={1.5}>
          <Stack
            sx={{
              pl: 3,
              borderLeft: '4px solid',
              borderColor: xBorderColor,
              px: 2
            }}>
            <Typography variant="body2" color="textPrimary">
              Alocação no fundo sem restrições, observados os limites regulamentares.
            </Typography>
          </Stack>
          <Stack
            sx={{
              pl: 3,
              borderLeft: '4px solid',
              borderColor: xBorderColor,
              px: 2
            }}>
            <Typography variant="body2" color="textPrimary">
              Alocação no fundo com restrições definidas pela instância devida da EFPC.{' '}
            </Typography>
          </Stack>
          <Stack
            sx={{
              pl: 3,
              borderLeft: '4px solid',
              borderColor: xBorderColor,
              px: 2
            }}>
            <Typography variant="body2" color="textPrimary">
              Vedação a alocações, avaliando desinvestimento do fundo.
            </Typography>
          </Stack>
        </Stack>
      </Stack>
      <Divider orientation="vertical" flexItem />
      <Stack sx={{ minWidth: '300px', maxWidth: '340px', width: '100%' }}>
        <Stack gap={2} direction="row" alignItems="center" justifyContent="space-between" mb={1}>
          <Stack direction="row" gap={2} alignItems="baseline">
            <Typography
              color="textSecondary"
              variant="caption"
              sx={{ textTransform: 'uppercase', fontWeight: 500 }}>
              Dados do Fundo
            </Typography>
          </Stack>
        </Stack>
        <Stack gap={0.5}>
          {dadosAtivo.map(pItem => (
            <Typography key={pItem.label} variant="body2" color="textPrimary" gutterBottom>
              <b>{pItem.label}:</b> {pItem.value}
            </Typography>
          ))}
          {dadosAtivo.length === 0 && (
            <Typography variant="body2" color="textSecondary">
              Dados do fundo não disponíveis.
            </Typography>
          )}
        </Stack>
      </Stack>
      <Divider orientation="vertical" flexItem />
      <Stack sx={{ minWidth: '200px', maxWidth: '340px', width: '100%' }}>
        <Stack gap={2} direction="row" alignItems="center" justifyContent="space-between" mb={1}>
          <Stack direction="row" gap={2} alignItems="baseline">
            <Typography
              color="textSecondary"
              variant="caption"
              sx={{ textTransform: 'uppercase', fontWeight: 500 }}>
              Dados dos Gestor
            </Typography>
          </Stack>
        </Stack>
        <Stack gap={0.5}>
          {dadosGestor.map(item => (
            <Typography key={item.label} variant="body2" color="textPrimary" gutterBottom>
              <b>{item.label}:</b> {item.value}
            </Typography>
          ))}
          {dadosGestor.length === 0 && (
            <Typography variant="body2" color="textSecondary">
              Dados cadastrais não disponíveis.
            </Typography>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
});

export default AvaliacoesOutputResumo;
